import {
  Box,
  Divider,
  Grid,
  TextField,
  Typography
  } from "@mui/material";
import { CategoryTypes } from "../../../interfaces/enums";
import { PresentationFormFieldsProps } from "../types/PresentationFormTypes";
import { useMemo } from "react";

const MAX_FIELD_LENGTH = 50;
/**
 * ============================================================================
 * PRESENTATION FORM FIELDS - KOMPONENT PÓL FORMULARZA PREZENTACJI
 * ============================================================================
 *
 * CEL KOMPONENTU:
 * - Wyświetla pola tekstowe formularza prezentacji (nazwa, tytuł)
 * - Obsługuje różne wymagania dla różnych typów kategorii
 * - Umożliwia edycję lub tylko podgląd pól w zależności od trybu
 * - Waliduje wymagalność pól na podstawie konfiguracji kategorii
 *
 * ARCHITEKTURA:
 * - Presentational Component: Tylko wyświetla pola, nie zarządza stanem
 * - Conditional Rendering: Pokazuje pola tylko dla odpowiednich typów kategorii
 * - Material-UI: Używa komponentów MUI do spójnego wyglądu
 * - Responsive: Dostosowuje się do różnych rozmiarów ekranu
 *
 * PRZEPŁYW DANYCH:
 * 1. Otrzymuje dane formularza i kategorię przez props
 * 2. Sprawdza typ kategorii (SOLO/DUO/TRIO nie mają pól tekstowych)
 * 3. Wyświetla odpowiednie pola na podstawie wymagań kategorii
 * 4. Obsługuje zmiany w polach przez callback onFormDataChange
 *
 * SPECYFIKA TYPÓW KATEGORII:
 * - SOLO: Brak pól tekstowych (nazwa generowana automatycznie)
 * - DUO: Brak pól tekstowych (nazwa generowana automatycznie)
 * - TRIO: Brak pól tekstowych (nazwa generowana automatycznie)
 * - OTHER: Pola nazwy i tytułu (jeśli wymagane)
 *
 * POLA FORMULARZA:
 * - Nazwa zespołu: Wymagana dla kategorii OTHER
 * - Tytuł choreografii: Wymagany jeśli category.isTitleRequired = true
 */

export default function PresentationFormFields({
  formData,
  onFormDataChange,
  category,
  editableMode = true,
}: PresentationFormFieldsProps) {
  // ============================================================================
  // WALIDACJA TYPU KATEGORII
  // ============================================================================

  // Dla kategorii SOLO, DUO i TRIO nie pokazuj pól tekstowych
  // Nazwy prezentacji są generowane automatycznie na podstawie zawodników
  if (
    category?.type === CategoryTypes.SOLO ||
    category?.type === CategoryTypes.DUO ||
    category?.type === CategoryTypes.TRIO
  ) {
    return null;
  }

  // ============================================================================
  // WALIDACJA DŁUGOŚCI PÓL
  // ============================================================================

  // Walidacja nazwy zespołu
  const nameError = useMemo(() => {
    const name = formData.name || "";
    if (!name.trim()) {
      return "Nazwa zespołu jest wymagana";
    }
    if (name.trim().length > MAX_FIELD_LENGTH) {
      return `Nazwa zespołu nie może mieć więcej niż ${MAX_FIELD_LENGTH} znaków`;
    }
    return null;
  }, [formData.name]);

  // Walidacja tytułu choreografii
  const titleError = useMemo(() => {
    if (!category?.isTitleRequired) return null;
    const title = formData.title || "";
    if (!title.trim()) {
      return "Tytuł choreografii jest wymagany";
    }
    if (title.trim().length > MAX_FIELD_LENGTH) {
      return `Tytuł choreografii nie może mieć więcej niż ${MAX_FIELD_LENGTH} znaków`;
    }
    return null;
  }, [formData.title, category?.isTitleRequired]);

  // ============================================================================
  // RENDEROWANIE PÓL FORMULARZA DLA KATEGORII OTHER
  // ============================================================================

  return (
    <>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* ========================================================================
            POLE NAZWA ZESPOŁU
            ======================================================================== */}

        <Grid item xs={12} sm={6}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#2f2f2f" }}>
              Nazwa zespołu (wymagane)
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.name || ""}
              onChange={(e) => onFormDataChange("name", e.target.value)}
              error={!!nameError}
              helperText={nameError || `Nazwa zespołu (max ${MAX_FIELD_LENGTH} znaków)`}
              placeholder="Wprowadź nazwę zespołu"
              disabled={!editableMode}
            />
          </Box>
        </Grid>

        {/* ========================================================================
            POLE TYTUŁ CHOREOGRAFII (OPCJONALNE)
            ======================================================================== */}

        {/* Wyświetl pole tytułu tylko jeśli jest wymagane w kategorii */}
        {category?.isTitleRequired && (
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#2f2f2f" }}>
                Tytuł choreografii (wymagane)
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.title || ""}
                onChange={(e) => onFormDataChange("title", e.target.value)}
                error={!!titleError}
                helperText={titleError || `Tytuł choreografii (max ${MAX_FIELD_LENGTH} znaków)`}
                placeholder="Wprowadź tytuł choreografii"
                disabled={!editableMode}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* ========================================================================
          ROZDZIELACZ WIZUALNY
          ======================================================================== */}

      {/* Separator między polami formularza a resztą komponentu */}
      <Divider sx={{ my: 3 }} />
    </>
  );
}
